import React, { useState } from 'react'
import {
    Theme,
    TextField,
    Button,
    ListItem,
    ListItemText,
    IconButton,
    Collapse,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Tooltip,
} from '@material-ui/core'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { Container } from '../../../components'
import {
    CriteriaGroup,
    getEntityClassificationUiName,
    MatchType,
} from '../../../models'
import {
    Add as AddIcon,
    Check as CheckIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    RadioButtonUnchecked as RadioButtonUncheckedIcon,
    FindInPage as FindInPageIcon,
    RemoveFromQueue as RemoveFromQueueIcon,
    AddToQueue as AddToQueueIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@material-ui/icons'
import { SearchField } from '../../../components/SearchField'

interface SettingsPageProps {
    theme: Theme
    height: number
    criteriaGroups: CriteriaGroup[]
    selectedGroupId?: number
    onSelectGroup: (groupId: number) => void
    onUpdateMatchType?: (
        groupId: number,
        criteriaId: number,
        matchType: MatchType,
    ) => void
    onUpdateDisplayField?: (
        groupId: number,
        criteriaId: number,
        display: boolean,
    ) => void
    onUpdateDescription?: (
        groupId: number,
        criteriaId: number,
        prompt_description: string,
    ) => void
    onUpdateOrder?: (
        groupId: number,
        reorderedCriteria: {
            entity_class_id: number
            match_type?: MatchType
            display?: boolean
            prompt_description?: string
            order?: number
        }[],
    ) => void
    onCreateGroup?: (name: string) => void
}

export const SettingsPage = ({
    theme,
    height,
    criteriaGroups,
    selectedGroupId,
    onSelectGroup,
    onUpdateMatchType,
    onUpdateDisplayField,
    onUpdateOrder,
    onUpdateDescription,
    onCreateGroup,
}: SettingsPageProps) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [newGroupName, setNewGroupName] = useState('')

    const handleCreateGroup = () => {
        if (newGroupName.trim() && onCreateGroup) {
            onCreateGroup(newGroupName.trim())
            setNewGroupName('')
            setIsDialogOpen(false)
        }
    }

    return (
        <Container
            style={{
                display: 'flex',
                flexDirection: 'column',
                height,
                overflow: 'auto', // Enable scrolling
            }}
        >
            <div style={{ flex: 1, overflow: 'auto' }}>
                {' '}
                {/* Scrollable content area */}
                {criteriaGroups.map((group) => (
                    <GroupRow
                        key={group.id}
                        group={group}
                        theme={theme}
                        onSelectGroup={onSelectGroup}
                        isSelected={group.id === selectedGroupId}
                        onUpdateMatchType={onUpdateMatchType}
                        onUpdateDisplayField={onUpdateDisplayField}
                        onUpdateOrder={onUpdateOrder}
                        onUpdateDescription={onUpdateDescription}
                    />
                ))}
            </div>
            <ListItem // This will stay at the bottom
                button
                onClick={() => setIsDialogOpen(true)}
                style={{
                    marginTop: '8px',
                    border: `1px dashed ${theme.palette.text.disabled}`,
                    borderRadius: '4px',
                    flexShrink: 0, // Prevent button from shrinking
                }}
            >
                <AddIcon style={{ marginRight: 8 }} />
                <ListItemText primary="Add New Criteria Group" />
            </ListItem>

            <Dialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Create New Criteria Group</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Group Name"
                        type="text"
                        fullWidth
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleCreateGroup()
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setIsDialogOpen(false)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateGroup}
                        color="primary"
                        disabled={!newGroupName.trim()}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

const GroupRow = (props: {
    group: CriteriaGroup
    theme: Theme
    onSelectGroup: (groupId: number) => void
    isSelected: boolean
    onUpdateMatchType?: (
        groupId: number,
        criteriaId: number,
        matchType: MatchType,
    ) => void
    onUpdateDisplayField?: (
        groupId: number,
        criteriaId: number,
        display: boolean,
    ) => void
    onUpdateDescription?: (
        groupId: number,
        criteriaId: number,
        prompt_description: string,
    ) => void
    onUpdateOrder?: (
        groupId: number,
        reorderedCriteria: {
            entity_class_id: number
            match_type?: MatchType
            display?: boolean
            prompt_description?: string
            order?: number
        }[],
    ) => void
}) => {
    const [expanded, setExpanded] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showHidden, setShowHidden] = useState(false)
    const [
        descPopoverAnchor,
        setDescPopoverAnchor,
    ] = useState<null | HTMLElement>(null)
    const [editingDescriptionId, setEditingDescriptionId] = useState<
        number | null
    >(null)
    const [descriptionDraft, setDescriptionDraft] = useState('')

    console.log('props.group', props.group)

    // Sort by order property
    const sortedCriteria = [...props.group.match_criteria].sort(
        (a, b) => a.order - b.order,
    )

    // Filter by search and visibility
    const filteredCriteria = sortedCriteria.filter((criteria) => {
        // First apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase()
            const matchesSearch =
                getEntityClassificationUiName(criteria.entity_class.name)
                    .toLowerCase()
                    .includes(term) ||
                (criteria.entity_class.description || '')
                    .toLowerCase()
                    .includes(term)
            if (!matchesSearch) return false
        }

        // Then apply visibility filter
        return showHidden || criteria.display
    })

    const hiddenCriteriaCount = sortedCriteria.filter((c) => !c.display).length

    // Drag and drop handler
    const handleDragEnd = (result: any) => {
        if (!result.destination) return
        const { source, destination } = result

        // Reorder filteredCriteria
        const reordered = Array.from(filteredCriteria)
        const [removed] = reordered.splice(source.index, 1)
        reordered.splice(destination.index, 0, removed)

        // Create the complete reordered list of criteria with their entity_class_ids
        const reorderedCriteria = reordered.map((criteria, index) => ({
            entity_class_id: criteria.entity_class.id,
            match_type: criteria.match_type,
            display: criteria.display,
            prompt_description: criteria.prompt_description,
            order: index + 1,
        }))

        // Always send the complete reordered list
        props.onUpdateOrder?.(props.group.id, reorderedCriteria)
    }

    const handleEditDescription = (
        criteria: any,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        event.stopPropagation()
        setEditingDescriptionId(criteria.entity_class.id)
        setDescriptionDraft(criteria.prompt_description || '')
        setDescPopoverAnchor(event.currentTarget)
    }

    const handleSaveDescription = () => {
        if (editingDescriptionId) {
            props.onUpdateDescription?.(
                props.group.id,
                editingDescriptionId,
                descriptionDraft,
            )
        }
        setDescPopoverAnchor(null)
        setEditingDescriptionId(null)
    }

    const handleClosePopover = () => {
        setDescPopoverAnchor(null)
        setEditingDescriptionId(null)
    }

    const CriteriaCard = ({
        criteria,
        index,
    }: {
        criteria: any
        index: number
    }) => {
        const isExtracting = criteria.match_type === 'EXTRACT'
        const isVisible = criteria.display
        return (
            <Draggable
                key={criteria.id}
                draggableId={criteria.id.toString()}
                index={index}
            >
                {(provided) => (
                    <Paper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 0px',
                            marginBottom: 2,
                            marginLeft: 4,
                            width: '100%',
                            boxSizing: 'border-box',
                            backgroundColor: isExtracting
                                ? 'rgba(205, 233, 221, 0.7)'
                                : 'transparent',
                            ...provided.draggableProps.style,
                        }}
                    >
                        {/* Drag handle */}
                        <div
                            {...provided.dragHandleProps}
                            style={{
                                cursor: 'grab',
                                marginRight: 12,
                                fontSize: 24,
                                marginLeft: 4,
                                borderLeft: '1px solid #ccc',
                            }}
                        >
                            ≡
                        </div>
                        {/* Visibility toggle */}
                        {isVisible ? (
                            <IconButton
                                size="small"
                                onClick={() =>
                                    props.onUpdateDisplayField?.(
                                        props.group.id,
                                        criteria.entity_class.id,
                                        !isVisible,
                                    )
                                }
                                style={{ marginLeft: 0, marginRight: 4 }}
                                title={
                                    isVisible
                                        ? 'Hide from Table'
                                        : 'Show in Table'
                                }
                            >
                                <RemoveFromQueueIcon />
                            </IconButton>
                        ) : (
                            <IconButton
                                size="small"
                                onClick={() =>
                                    props.onUpdateDisplayField?.(
                                        props.group.id,
                                        criteria.entity_class.id,
                                        !isVisible,
                                    )
                                }
                                style={{ marginLeft: 0, marginRight: 4 }}
                                title={
                                    isVisible
                                        ? 'Hide from Table'
                                        : 'Show in Table'
                                }
                            >
                                <AddToQueueIcon />
                            </IconButton>
                        )}
                        {/* Extract toggle */}
                        <IconButton
                            size="small"
                            onClick={() =>
                                props.onUpdateMatchType?.(
                                    props.group.id,
                                    criteria.entity_class.id,
                                    isExtracting
                                        ? ('NOT_REQUIRED' as MatchType)
                                        : ('EXTRACT' as MatchType),
                                )
                            }
                            style={{ marginLeft: 0, marginRight: 12 }}
                            title={
                                isExtracting
                                    ? 'Stop Extracting from Lease'
                                    : 'Extract from Lease'
                            }
                        >
                            <FindInPageIcon />
                        </IconButton>
                        {/* Name */}
                        <Typography
                            style={{
                                fontWeight: 'bold',
                                marginRight: 16,
                                minWidth: 200,
                                maxWidth: 200,
                                width: 200,
                            }}
                        >
                            {getEntityClassificationUiName(
                                criteria.entity_class.name,
                            )}
                        </Typography>
                        {/* Description */}
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: 300,
                                maxWidth: 800,
                                width: '100%',
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) =>
                                    handleEditDescription(criteria, e)
                                }
                            >
                                <span role="img" aria-label="edit">
                                    ✎
                                </span>
                            </IconButton>
                            <Typography
                                variant="body2"
                                style={{
                                    flex: 1,
                                    marginRight: 4,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {criteria.prompt_description ||
                                    'No description'}
                            </Typography>
                        </div>
                    </Paper>
                )}
            </Draggable>
        )
    }

    return (
        <>
            <ListItem
                button
                selected={props.isSelected}
                onClick={(e) => {
                    if (!(e.target as HTMLElement).closest('button')) {
                        setExpanded(!expanded)
                    }
                }}
                style={{
                    backgroundColor: props.isSelected
                        ? props.theme.palette.action.selected
                        : props.theme.palette.background.default,
                }}
            >
                <IconButton onClick={() => props.onSelectGroup(props.group.id)}>
                    {props.isSelected ? (
                        <CheckIcon />
                    ) : (
                        <RadioButtonUncheckedIcon />
                    )}
                </IconButton>
                <ListItemText primary={props.group.name} />
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItem>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <div>
                    <div
                        style={{
                            marginBottom: '16px',
                            marginLeft: 30,
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                        }}
                    >
                        <div style={{ marginRight: '16px' }}>
                            <IconButton
                                size="small"
                                onClick={() => setShowHidden(!showHidden)}
                            >
                                {showHidden ? (
                                    <Tooltip title="Hide Hidden Items">
                                        <VisibilityOffIcon />
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Show Hidden Items">
                                        <VisibilityIcon />
                                    </Tooltip>
                                )}
                            </IconButton>
                        </div>
                        <SearchField
                            size="small"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ width: '300px' }}
                        />
                    </div>
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="criteria-list">
                            {(provided) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {filteredCriteria.map((criteria, idx) => (
                                        <div
                                            key={criteria.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                margin: 0,
                                                padding: 0,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 24,
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-end',
                                                    marginRight: 4,
                                                    color: '#888',
                                                    fontWeight: 500,
                                                    fontSize: 16,
                                                    userSelect: 'none',
                                                    padding: 0,
                                                }}
                                            >
                                                {idx + 1}
                                            </div>
                                            <CriteriaCard
                                                criteria={criteria}
                                                index={idx}
                                            />
                                        </div>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </Collapse>
            <Dialog
                open={Boolean(editingDescriptionId)}
                onClose={handleClosePopover}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Edit Description</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Description"
                        value={descriptionDraft}
                        onChange={(e) => setDescriptionDraft(e.target.value)}
                        fullWidth
                        multiline
                        variant="outlined"
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleSaveDescription}
                        color="primary"
                        variant="contained"
                        size="small"
                    >
                        Save
                    </Button>
                    <Button onClick={handleClosePopover} size="small">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
